import { useRef, useState, useEffect } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Grid,
  Heart,
  Coffee,
  Car,
  Laptop,
  BookOpen,
  Film,
  ShoppingBag,
  Plane,
} from 'lucide-react';

interface Category {
  id: string;
  name: string;
  icon: string;
}

interface CategoryStripProps {
  categories: Category[];
  selectedCategory: string;
  onCategorySelect: (category: string) => void;
}

const iconMap: Record<string, React.ReactNode> = {
  Grid: <Grid className="w-6 h-6" />,
  Heart: <Heart className="w-6 h-6" />,
  Coffee: <Coffee className="w-6 h-6" />,
  Car: <Car className="w-6 h-6" />,
  Laptop: <Laptop className="w-6 h-6" />,
  BookOpen: <BookOpen className="w-6 h-6" />,
  Film: <Film className="w-6 h-6" />,
  ShoppingBag: <ShoppingBag className="w-6 h-6" />,
  Plane: <Plane className="w-6 h-6" />,
};

const CategoryStrip = ({ categories, selectedCategory, onCategorySelect }: CategoryStripProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollButtons = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth
      );
    }
  };

  useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 300;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="relative bg-card rounded-lg shadow-sm border border-border p-4">
      {/* Left Scroll Button */}
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 ml-2 hover:bg-gray-50 transition-colors duration-150"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
      )}

      {/* Categories Container */}
      <div
        ref={scrollContainerRef}
        className="flex space-x-3 overflow-x-auto scrollbar-hide scroll-smooth"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {categories.map((category) => {
          const isSelected = selectedCategory === category.name;
          return (
            <button
              key={category.id}
              onClick={() => onCategorySelect(category.name)}
              className={`
                flex-shrink-0 flex flex-col items-center justify-center min-w-[100px] px-4 py-3 rounded-lg
                transition-all duration-200
                ${isSelected
                  ? 'bg-primary text-white shadow-md'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }
              `}
            >
              <div className={`mb-2 ${isSelected ? 'text-white' : 'text-gray-600'}`}>
                {iconMap[category.icon] || <Grid className="w-6 h-6" />}
              </div>
              <span className="text-sm font-medium whitespace-nowrap">
                {category.name}
              </span>
            </button>
          );
        })}
      </div>

      {/* Right Scroll Button */}
      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 mr-2 hover:bg-gray-50 transition-colors duration-150"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      )}
    </div>
  );
};

export default CategoryStrip;
